import { useReferenceRecentApprovedInvoiceReports } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import type { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import { DATE_FORMAT } from '@moneyforward/ap-frontend-components';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useColumns } from './columns';

export type InvoiceReportItem = {
  id: string;
  number_without_prefix: number;
  title: string;
  office_member?: { display_name?: string };
  created_at?: string;
  invoice_file?: { id?: string; name?: string; cache_content_url?: string };
  total_amount?: string | null;
  foreign_currency_amount?: string | null;
};

export interface PaymentRequestRow {
  id: string;
  numberWithoutPrefix: string;
  title: string;
  payee: string;
  totalAmount: string;
  foreignCurrencyAmount: string | null;
  createdAt: string;
  invoiceId: string;
  mfFileId: string;
  fileName: string;
  fileUrl: string | undefined;
}

export const formatPaymentRequestRow = (
  item: InvoiceReportItem
): PaymentRequestRow => {
  // BE returns "-" when transfer amount is zero (mirrors SearchedInvoiceReport).
  // The Apply Past Line Item modal renders blank instead of "-".
  const rawTotalAmount = item.total_amount ?? '';
  const totalAmount = rawTotalAmount === '-' ? '' : rawTotalAmount;

  return {
    id: item.id,
    numberWithoutPrefix: String(item.number_without_prefix),
    title: item.title,
    payee: item.office_member?.display_name || '',
    totalAmount,
    foreignCurrencyAmount: item.foreign_currency_amount ?? null,
    createdAt: item.created_at
      ? dayjs(item.created_at).format(DATE_FORMAT)
      : '',
    invoiceId: item.id,
    mfFileId: item.invoice_file?.id || '',
    fileName: item.invoice_file?.name || '',
    fileUrl: item.invoice_file?.cache_content_url || undefined,
  };
};

export const usePaymentRequestTable = (payeeId?: string) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useReferenceRecentApprovedInvoiceReports(
    payeeId || ''
  );

  const tableData: PaymentRequestRow[] = useMemo(
    () => (data?.data?.invoice_reports || []).map(formatPaymentRequestRow),
    [data]
  );

  const columns = useColumns();

  const notification: InlineNotificationProps = useMemo(
    () => ({
      visible: isCommonError(error ?? {}),
      type: 'error',
      message:
        isCommonError(error ?? {}) && Array.isArray(error?.messages)
          ? error?.messages.join('')
          : String(error?.messages) ?? t('system_error'),
    }),
    [error, t]
  );

  return {
    columns,
    tableData,
    isLoading,
    notification,
  };
};
